import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Trash, ArrowRight, ShieldCheck, Receipt, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Cart() {
  const { fetchCartCount } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const fetchCart = async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchCart(); }, []);

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/cart/${itemId}`);
      toast.success("Removed from cart");
      fetchCart();
      fetchCartCount();
    } catch { toast.error("Failed to remove"); }
  };

  const handleCheckout = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/cart/checkout", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally { setChecking(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin" /></div>;

  const empty = !cart || !cart.items || cart.items.length === 0;

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#2C5545]/10 flex items-center justify-center">
            <ShoppingCart size={24} weight="duotone" className="text-[#2C5545]" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight" data-testid="cart-heading">Your Cart</h1>
            <p className="text-sm text-[#5C665D]">{empty ? "No items" : `${cart.items.length} item${cart.items.length > 1 ? "s" : ""}`}</p>
          </div>
        </div>

        {empty ? (
          <div className="text-center py-20 bg-white border border-[#EAE7E0] rounded-2xl" data-testid="cart-empty">
            <ShoppingCart size={48} weight="duotone" className="text-[#EAE7E0] mx-auto mb-4" />
            <p className="font-heading text-2xl text-[#1A1C1A] mb-2">Your cart is empty</p>
            <p className="text-sm text-[#5C665D] mb-6">Add items from the browse page to start renting.</p>
            <Link to="/browse" className="bg-[#2C5545] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#224235] transition-all inline-flex items-center gap-2" data-testid="cart-browse-link">
              Browse Items <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item, idx) => {
                const img = item.item_image ? `${API_URL}/api/files/${item.item_image}` : null;
                const itemServiceFee = (item.subtotal * (cart.service_fee_rate || 0.05)).toFixed(2);
                const itemTax = (item.subtotal * (item.tax_rate || 0)).toFixed(2);
                return (
                  <div key={idx} className="bg-white border border-[#EAE7E0] rounded-2xl p-5" data-testid={`cart-item-${item.item_id}`}>
                    <div className="flex gap-4">
                      {img && <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#F5F3EE] flex-shrink-0"><img src={img} alt="" className="w-full h-full object-cover" /></div>}
                      <div className="flex-1 min-w-0">
                        <Link to={`/item/${item.item_id}`} className="text-sm font-medium text-[#1A1C1A] hover:text-[#2C5545]">{item.item_title}</Link>
                        <p className="text-xs text-[#5C665D] mt-0.5 flex items-center gap-1"><MapPin size={10} weight="duotone" />{item.location}</p>
                        <p className="text-xs text-[#5C665D] mt-0.5">{item.start_date} to {item.end_date} ({item.days} days)</p>
                      </div>
                      <button onClick={() => removeItem(item.item_id)} className="p-2 text-[#D94A4A] hover:bg-red-50 rounded-xl transition-colors self-start" data-testid={`cart-remove-${item.item_id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                    {/* Per-item breakdown */}
                    <div className="mt-3 pt-3 border-t border-[#EAE7E0]/50 space-y-1 text-xs text-[#5C665D]">
                      <div className="flex justify-between"><span>${item.price_per_day?.toFixed(2)} x {item.days} days</span><span>${item.subtotal?.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>Service fee (5%)</span><span>${itemServiceFee}</span></div>
                      <div className="flex justify-between"><span>Tax ({((item.tax_rate || 0) * 100).toFixed(2)}%)</span><span>${itemTax}</span></div>
                      {item.insurance_fee > 0 && <div className="flex justify-between"><span>Insurance</span><span>${item.insurance_fee?.toFixed(2)}</span></div>}
                      {item.deposit > 0 && <div className="flex justify-between"><span>Deposit</span><span>${item.deposit?.toFixed(2)}</span></div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-[#EAE7E0] rounded-2xl p-6 sticky top-24" data-testid="cart-summary">
                <h2 className="font-heading text-xl font-medium text-[#1A1C1A] mb-4 flex items-center gap-2">
                  <Receipt size={20} weight="duotone" className="text-[#2C5545]" /> Order Summary
                </h2>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between text-[#5C665D]"><span>Subtotal</span><span>${cart.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between text-[#5C665D]"><span>Service fee (5%)</span><span>${cart.service_fee?.toFixed(2)}</span></div>
                  <div className="flex justify-between text-[#5C665D]"><span>Tax</span><span>${cart.tax?.toFixed(2)}</span></div>
                  {cart.insurance > 0 && <div className="flex justify-between text-[#5C665D]"><span>Insurance</span><span>${cart.insurance?.toFixed(2)}</span></div>}
                  {cart.deposits > 0 && (
                    <div className="flex justify-between text-[#5C665D]"><span className="flex items-center gap-1"><ShieldCheck size={12} weight="duotone" />Deposits</span><span>${cart.deposits?.toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between font-semibold text-[#1A1C1A] pt-3 border-t border-[#EAE7E0] text-base">
                    <span>Total</span><span>${cart.total?.toFixed(2)}</span>
                  </div>
                </div>
                <button onClick={handleCheckout} disabled={checking} className="w-full mt-6 bg-[#2C5545] text-white hover:bg-[#224235] rounded-full py-3.5 font-medium transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="cart-checkout-btn">
                  {checking ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><ArrowRight size={18} weight="bold" /> Checkout</>}
                </button>
                <Link to="/browse" className="block text-center text-sm text-[#2C5545] font-medium mt-3 hover:underline" data-testid="cart-continue-link">Continue Browsing</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
